import React, { useState, useEffect } from 'react';
import { Dropdown, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faCheck } from '@fortawesome/free-solid-svg-icons';
import axiosClient from 'api/axiosClient';

interface Company {
  id: number;
  name: string;
}

interface CompanySelectorProps {
  userRole: string;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({ userRole }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);

  const isSuperAdmin = userRole === 'super_user';

  useEffect(() => {
    if (isSuperAdmin) {
      loadCompanies();
    }
  }, [isSuperAdmin]);

  // Load current context after companies are loaded
  useEffect(() => {
    if (isSuperAdmin && companies.length > 0) {
      loadCurrentContext();
    }
  }, [companies, isSuperAdmin]);

  const loadCompanies = async () => {
    try {
      const response = await axiosClient.get('/companies/');

      setCompanies(response.data);
    } catch (error) {
      console.error('❌ Failed to load companies:', error);
    }
  };

  const loadCurrentContext = async () => {
    try {
      const response = await axiosClient.get('/context/effective-company');

      if (response.data.company_id) {
        // Fetch the company name from the companies list
        const company = companies.find(
          (c: Company) => c.id === response.data.company_id
        );

        setCurrentCompany(company || null);
      } else {
        setCurrentCompany(null);
      }
    } catch (error) {
      console.error('❌ Failed to load current context:', error);
    }
  };

  const handleCompanySwitch = async (companyId: number | null) => {
    setLoading(true);
    try {
      if (companyId === null) {
        await axiosClient.post('/context/reset');
      } else {
        await axiosClient.post('/context/set-company', {
          company_id: companyId
        });
      }
      // Reload the page to refresh all data
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch company:', error);
      setLoading(false);
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="d-none d-md-flex align-items-center gap-2">
      {currentCompany && (
        <Badge bg="info" className="px-2 py-1 d-none d-xl-inline-block">
          🔍 Viewing: {currentCompany.name}
        </Badge>
      )}
      <Dropdown>
        <Dropdown.Toggle
          variant="phoenix-secondary"
          id="company-selector-dropdown"
          size="sm"
          disabled={loading}
          className="text-truncate"
          style={{ maxWidth: '200px' }}
        >
          <FontAwesomeIcon icon={faBuilding} />
          <span className="d-none d-lg-inline ms-2">
            {currentCompany ? currentCompany.name : 'All Companies'}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu
          align="end"
          className="company-selector-dropdown"
          style={{ maxHeight: '400px', overflowY: 'auto' }}
        >
          <Dropdown.Item
            onClick={() => handleCompanySwitch(null)}
            className="d-flex align-items-center justify-content-between"
          >
            <span>All Companies</span>
            {!currentCompany && (
              <FontAwesomeIcon icon={faCheck} className="text-success ms-2" />
            )}
          </Dropdown.Item>
          <Dropdown.Divider />
          {companies.map(company => (
            <Dropdown.Item
              key={company.id}
              onClick={() => handleCompanySwitch(company.id)}
              className="d-flex align-items-center justify-content-between"
            >
              <span>{company.name}</span>
              {currentCompany?.id === company.id && (
                <FontAwesomeIcon icon={faCheck} className="text-success ms-2" />
              )}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default CompanySelector;
